import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { usePageMeta } from '@/hooks/useSEO';
import { ArrowRight, ArrowUpRight, CheckCircle2, Trophy, Scale, ChevronDown, GraduationCap } from 'lucide-react';
import { COMPARISONS } from './comparisonData';

/**
 * ComparisonPage — `/study-abroad/comparison/:slug/`
 * Data-driven head-to-head destination comparison. Content in comparisonData.js.
 */

const WinnerChip = ({ side, comp }) => {
  if (side === 'tie') {
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase text-slate-500">Even</span>;
  }
  const c = comp[side];
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase text-emerald-700">
      <Trophy className="w-3 h-3" aria-hidden="true" /> {c.name}
    </span>
  );
};

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid={`cmp-faq-toggle-${idx}`}
        aria-expanded={open}
      >
        <span className="text-sm sm:text-base font-semibold text-[#003087]">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && <p className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
};

const ComparisonPage = () => {
  const { slug } = useParams();
  const comp = COMPARISONS[slug];

  usePageMeta(
    comp ? comp.metaTitle : 'Comparison Not Found | Zafco Study Abroad',
    comp ? comp.metaDescription : '',
    comp ? { canonical: `/study-abroad/comparison/${slug}/` } : undefined,
  );

  useEffect(() => {
    if (!comp) return undefined;
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: comp.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const bc = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Study Abroad', item: 'https://zafco.in/study-abroad/' },
        { '@type': 'ListItem', position: 3, name: 'Comparisons', item: 'https://zafco.in/study-abroad/comparison/' },
        { '@type': 'ListItem', position: 4, name: comp.title },
      ],
    };
    const s1 = document.createElement('script');
    const s2 = document.createElement('script');
    s1.type = 'application/ld+json'; s1.setAttribute('data-cmp-faq-schema', '1'); s1.text = JSON.stringify(faqSchema);
    s2.type = 'application/ld+json'; s2.setAttribute('data-cmp-bc-schema', '1'); s2.text = JSON.stringify(bc);
    document.head.appendChild(s1);
    document.head.appendChild(s2);
    return () => {
      document.head.querySelector('script[data-cmp-faq-schema]')?.remove();
      document.head.querySelector('script[data-cmp-bc-schema]')?.remove();
    };
  }, [comp, slug]);

  if (!comp) return <Navigate to="/study-abroad/comparison/" replace />;

  const { a, b } = comp;
  const aWins = comp.rows.filter((r) => r.winner === 'a').length;
  const bWins = comp.rows.filter((r) => r.winner === 'b').length;

  return (
    <div className="bg-white zaf-prose" data-testid="comparison-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 55%, #020617 100%)' }}
      >
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C5A028]/14 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-24">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-5">
              <Scale className="w-3.5 h-3.5" aria-hidden="true" /> Head-to-Head · 2026 Data
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-[48px] font-bold font-syne leading-[1.12] tracking-tight mb-5" data-testid="cmp-h1">
              {comp.h1}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl mb-7">{comp.intro}</p>
            <div className="flex items-center gap-4 text-3xl sm:text-4xl" aria-hidden="true">
              <span>{a.flag}</span>
              <span className="text-[#FACC15] font-syne font-bold text-xl sm:text-2xl">vs</span>
              <span>{b.flag}</span>
            </div>
          </div>
        </div>
      </section>

      {/* VERDICT */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-3xl border-2 border-[#C5A028]/30 bg-gradient-to-br from-amber-50/40 via-white to-amber-50/40 p-6 sm:p-8 shadow-md" data-testid="cmp-verdict-box">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-2">Zafco Verdict</div>
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed">{comp.verdict}</p>
            <div className="flex flex-wrap gap-4 mt-5 pt-4 border-t border-amber-200/60 text-xs font-semibold text-slate-600">
              <span data-testid="cmp-score-a">{a.flag} {a.name}: wins {aWins} of {comp.rows.length} factors</span>
              <span data-testid="cmp-score-b">{b.flag} {b.name}: wins {bWins} of {comp.rows.length} factors</span>
            </div>
          </div>
        </div>
      </section>

      {/* TABLE */}
      <section className="bg-white py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold font-syne text-[#003087] leading-tight mb-8">
            Factor-by-Factor Breakdown
          </h2>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm" data-testid="cmp-table">
            <table className="w-full text-left border-collapse min-w-[640px]">
              <thead>
                <tr className="bg-[#001F5C] text-white">
                  <th className="px-4 py-3.5 text-xs font-bold tracking-wider uppercase w-[28%]">Factor</th>
                  <th className="px-4 py-3.5 text-xs font-bold tracking-wider uppercase w-[28%]">{a.flag} {a.name}</th>
                  <th className="px-4 py-3.5 text-xs font-bold tracking-wider uppercase w-[28%]">{b.flag} {b.name}</th>
                  <th className="px-4 py-3.5 text-xs font-bold tracking-wider uppercase">Edge</th>
                </tr>
              </thead>
              <tbody>
                {comp.rows.map((row, i) => (
                  <tr key={row.label} className={i % 2 ? 'bg-slate-50/70' : 'bg-white'}>
                    <td className="px-4 py-3.5 text-sm font-semibold text-[#003087] align-top">{row.label}</td>
                    <td className="px-4 py-3.5 text-sm text-slate-700 align-top">{row.a}</td>
                    <td className="px-4 py-3.5 text-sm text-slate-700 align-top">{row.b}</td>
                    <td className="px-4 py-3.5 align-top"><WinnerChip side={row.winner} comp={comp} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* DEEP DIVE */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-8">
          {comp.sections.map((s, i) => (
            <article key={s.heading} data-testid={`cmp-section-${i}`}>
              <h2 className="text-xl sm:text-2xl font-bold font-syne text-[#003087] leading-tight mb-3">{s.heading}</h2>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold font-syne text-[#003087] leading-tight mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3" data-testid="cmp-faq-list">
            {comp.faqs.map((f, i) => <FaqItem key={f.q} q={f.q} a={f.a} idx={i} />)}
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            {comp.related.map((r) => (
              <Link
                key={r.link}
                to={r.link}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold text-[#003087] hover:border-[#C5A028]/50 hover:bg-amber-50/40 transition-colors"
              >
                {r.label} <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="text-white" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 py-12 lg:py-16 text-center">
          <GraduationCap className="w-10 h-10 text-[#FACC15] mx-auto mb-4" aria-hidden="true" />
          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-3 leading-tight">
            Still torn between {a.name} and {b.name}?
          </h2>
          <p className="text-slate-300 max-w-xl mx-auto mb-6 leading-relaxed text-sm sm:text-base">
            Our counsellors map your budget, profile and timeline against both systems and give you a single, defensible recommendation — free.
          </p>
          <Link
            to={`/study-abroad/book-counselling/?source=comparison-${slug}`}
            className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C5A028]/30"
            data-testid="cmp-counselling-cta"
          >
            Get My Free Comparison Session <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ComparisonPage;
